import {
  AccrualDispute,
  ChargebackEscalation,
  ShortageEscalation,
  StagelessAction,
  type WorkPacket,
} from "src/types/work-packets";
import { isDefined } from "src/utils/isDefined";
import type { UpdatablePacketKey, UpdatableValue, UpdatableValues } from "../api/workPacketsAPI";
import { getUpdateParams } from "../../Cases/CaseDetail/hooks/getUpdateParams";
import dayjs from "dayjs";
import { getStagelessActionValue } from "../mappers";

// this is for both single update and bulk update
// stuff that is bulk update only should be undefined
const updateValueGetters: { [K in UpdatablePacketKey]: (packet: WorkPacket) => UpdatableValues[K] } = {
  currentAction: packet => packet.currentAction,
  currentPacketOwner: packet => packet.currentPacketOwner.id,
  backupPacketOwner: packet => packet.backupPacketOwner.id,
  monitoringPacketOwner: packet => ("monitoringPacketOwner" in packet ? packet.monitoringPacketOwner.id : undefined),
  notes: packet => packet.notes,
  isValidWorkPacket: packet => packet.isValidWorkPacket,
  evidenceType: packet => packet.evidenceType.join(","),
  evidenceAttachment: packet => packet.evidenceAttachment,
  disputes: packet => ("disputes" in packet ? packet.disputes : undefined),
  escalations: packet => ("escalations" in packet ? packet.escalations : undefined),
  disputeId: packet => ("disputeId" in packet ? packet.disputeId : undefined),
  clientDisputed: packet => ("clientDisputed" in packet ? packet.clientDisputed : undefined),
  vcDisputedInvoiceId: packet => ("vcDisputedInvoiceId" in packet ? packet.vcDisputedInvoiceId : undefined),
  disputeAmount: packet => ("disputeAmount" in packet ? packet.disputeAmount?.toString() : undefined),
  monitoringDate: packet => ("monitoringDate" in packet ? packet.monitoringDate?.format("YYYY-MM-DD") : undefined),
  deniedReason: packet => packet.deniedReason,

  // ignore abandoned reason if packet is not being marked as abandoned
  abandonedReason: packet =>
    packet.currentAction === getStagelessActionValue(StagelessAction.Abandoned) ? packet.abandonedReason : undefined,

  // bulk update only
  disputeCreatedDate: _packet => undefined,
  originalDisputedId: _packet => undefined,
  consolidatedDisputeId: _packet => undefined,
};

// --- comparison helpers ---
const isDifferentPrimitive = (a: unknown, b: unknown) => a !== b;
const isDifferentString = (a: unknown, b: unknown) =>
  (typeof a === "string" ? a.trim() : "") !== (typeof b === "string" ? b.trim() : "");
const isDifferentDayjs = (a: unknown, b: unknown) =>
  dayjs.isDayjs(a) && dayjs.isDayjs(b) ? a.format("YYYY-MM-DD") !== b.format("YYYY-MM-DD") : a !== b;

type DisputeFieldMapper<T> = {
  compare: (a: T, b: T) => boolean;
  map: (v: T) => T;
};

// Fully type-safe mappers referencing AccrualDispute
type DisputeFieldMappers = {
  [K in keyof AccrualDispute]?: DisputeFieldMapper<AccrualDispute[K]>;
};

const disputeFieldMappers: DisputeFieldMappers = {
  disputeAmount: {
    compare: isDifferentPrimitive,
    map: v => v,
  },
  disputeCreatedDate: {
    compare: isDifferentDayjs,
    map: v => v,
  },
  originalDisputeId: {
    compare: isDifferentString,
    map: v => (typeof v === "string" ? v.trim() : ""),
  },
  consolidatedDisputeId: {
    compare: isDifferentString,
    map: v => (typeof v === "string" ? v.trim() : ""),
  },
  disputeStatus: {
    compare: isDifferentPrimitive,
    map: v => v,
  },
  invoiceStatus: {
    compare: isDifferentPrimitive,
    map: v => v,
  },
  approvedAmount: {
    compare: isDifferentPrimitive,
    map: v => v,
  },
  disputeResolutionDate: {
    compare: isDifferentPrimitive,
    map: v => v,
  },
  paidAmount: {
    compare: isDifferentPrimitive,
    map: v => v,
  },
  reversalInvoiceNumber: {
    compare: isDifferentPrimitive,
    map: v => v,
  },
};

type Mapper<T> = (old: T, current: T) => T | undefined;

const mappers: { [K in UpdatablePacketKey]?: Mapper<UpdatableValue<K>> } = {
  disputes: (
    old: Partial<AccrualDispute>[],
    current: Partial<AccrualDispute>[],
  ): Partial<AccrualDispute>[] | undefined => {
    if (old.length !== current.length) {
      console.error("[getUpdatedFields] Dispute arrays differ in length");
      return [];
    }

    const updatedDisputes: Partial<AccrualDispute>[] = [];

    current.forEach((currentDispute, index) => {
      const oldDispute = old[index];
      if (!oldDispute) return;

      let hasChanges = false;
      const updatedDispute: Partial<AccrualDispute> = { id: currentDispute.id };

      (Object.keys(disputeFieldMappers) as (keyof typeof disputeFieldMappers)[]).forEach(field => {
        const fieldMapper = disputeFieldMappers[field] as DisputeFieldMapper<any>;
        if (!fieldMapper) return;
        const currentValue = currentDispute[field as keyof AccrualDispute];
        const oldValue = oldDispute[field as keyof AccrualDispute];
        if (fieldMapper.compare(currentValue, oldValue)) {
          (updatedDispute as any)[field] = fieldMapper.map(currentValue);
          hasChanges = true;
        }
      });

      if (hasChanges) {
        updatedDisputes.push(updatedDispute);
      }
    });

    return updatedDisputes.length > 0 ? updatedDisputes : undefined;
  },
  escalations: (
    old: (ShortageEscalation | ChargebackEscalation)[],
    current: (ShortageEscalation | ChargebackEscalation)[],
  ) => {
    const updatedEscalations = current.filter(
      (caseItem, index) =>
        areValuesDifferent(caseItem.approvedAmount, old[index].approvedAmount) ||
        areValuesDifferent(caseItem.approvedDate, old[index].approvedDate) ||
        areValuesDifferent(caseItem.status, old[index].status),
    );
    return updatedEscalations.length > 0 ? updatedEscalations : undefined;
  },
};

const areValuesDifferent = (a: unknown, b: unknown): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return true;
    return a.some((val, index) => areValuesDifferent(val, b[index]));
  } else if (typeof a === "object" && typeof b === "object" && a && b) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return true;
    return aKeys.some(key => areValuesDifferent(a[key as keyof typeof a], b[key as keyof typeof b]));
  } else {
    return a !== b;
  }
};

export const getUpdatedFields = (originalWorkPacket: WorkPacket, updatedWorkPacket: WorkPacket): UpdatableValues => {
  const updatedFieldEntries = Object.entries(updateValueGetters)
    .map(([key, getter]) => {
      const oldValue = getter(originalWorkPacket);
      const newValue = getter(updatedWorkPacket);
      if (newValue === undefined) return undefined;

      const isValueDifferent = areValuesDifferent(oldValue, newValue);
      if (!isValueDifferent) return undefined;

      const mapper = mappers[key as UpdatablePacketKey] as Mapper<typeof oldValue>;
      if (mapper) {
        const mappedValue = mapper(oldValue, newValue);
        if (mappedValue === undefined) return undefined;
        return [key, mappedValue];
      } else {
        return [key, newValue] as const;
      }
    })
    .filter(isDefined);
  const updatedFields: UpdatableValues = Object.fromEntries(updatedFieldEntries);
  if (
    "settlement" in originalWorkPacket &&
    "settlement" in updatedWorkPacket &&
    originalWorkPacket.settlement &&
    updatedWorkPacket.settlement
  ) {
    const updatedSettlementFields = getUpdateParams(originalWorkPacket.settlement, updatedWorkPacket.settlement, true);
    return { ...updatedFields, ...updatedSettlementFields };
  }

  return updatedFields;
};
